import React, { useState, useRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiChevronUp, FiChevronDown, FiCopy, FiTrash2, FiImage, FiPlus, FiX } from 'react-icons/fi';
import uploadApi from '_api/upload';
import { getUserInfo } from '_utils/localData';
import {
  SPLIT_CASE_CONDITION_FULL,
  SPLIT_CASE_CONDITION_PARTIAL,
} from '_utils/constant';
import OrderSettings from '../OrderSettings';
import SplitcasePurchase from '../SplitcasePurchase';
import SubItems from '../SubItems';
import { DEFAULT_ORDER_SETTINGS } from '../../hooks/useOrderSettings';
import { DEFAULT_SPLIT_CASE_SETTINGS } from '../../hooks/useSplitCasePurchase';
import './style.scss';

export default function ItemDetail({
  itemNumber = 1,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onSelectOption,
}) {
  const itemIndex = itemNumber - 1;
  const userInfo = getUserInfo();
  const {
    register,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useFormContext() || {};

  const itemError = errors?.items?.[itemIndex];
  const formSplitCase = watch ? watch(`items.${itemIndex}.splitCasePurchase`) : null;
  const isSplitCaseFull = Boolean(
    formSplitCase &&
      (formSplitCase.isFullCase ||
        formSplitCase.fulfillCondition === SPLIT_CASE_CONDITION_FULL ||
        formSplitCase.fulfilmentOption === 'full')
  );
  const splitCaseError = isSplitCaseFull
    ? itemError?.splitCasePurchase?.acknowledgementRequiredPayAfter?.message
    : null;
  const formImage = watch ? watch(`items.${itemIndex}.image`) : null;
  const itemPrice = watch ? watch(`items.${itemIndex}.price`) : 0;
  const formSubItems = watch ? watch(`items.${itemIndex}.subItems`) : null;
  const formMaxQuantity = watch ? watch(`items.${itemIndex}.maxQuantity`) : undefined;
  const formLimitPerOrder = watch ? watch(`items.${itemIndex}.limitPerOrder`) : undefined;
  const formMinOrderQty = watch ? watch(`items.${itemIndex}.minOrderQty`) : undefined;
  const formOrderCount = watch ? watch(`items.${itemIndex}.orderCount`) : 0;

  const initialImageUrl = typeof formImage === 'string' ? formImage : formImage?.url || null;

  const hasSplitCaseData = Boolean(
    formSplitCase &&
    (Number(formSplitCase.itemsPerCase) > 0 ||
      Number(formSplitCase.costPerItem) > 0 ||
      formSplitCase.name)
  );

  const hasSubItemsData = Boolean(
    formSubItems &&
    Array.isArray(formSubItems) &&
    formSubItems.length > 0
  );

  const hasOrderSettingsData = Boolean(
    (formMaxQuantity !== undefined && Number(formMaxQuantity) !== 1000) ||
    (formLimitPerOrder !== undefined && Number(formLimitPerOrder) !== 1000) ||
    (formMinOrderQty !== undefined && Number(formMinOrderQty) !== 1)
  );

  const [imagePreview, setImagePreview] = useState(initialImageUrl);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showOrderSettings, setShowOrderSettings] = useState(hasOrderSettingsData);
  const [showSplitCase, setShowSplitCase] = useState(hasSplitCaseData);
  const [showSubItems, setShowSubItems] = useState(hasSubItemsData);

  const dropdownRef = useRef(null);
  const subItemsRef = useRef(null);

  useEffect(() => {
    if (initialImageUrl !== imagePreview) {
      setImagePreview(initialImageUrl);
    }
  }, [initialImageUrl]);

  useEffect(() => {
    if (hasSplitCaseData && !showSplitCase) {
      setShowSplitCase(true);
    }
  }, [hasSplitCaseData]);

  useEffect(() => {
    if (hasSubItemsData && !showSubItems) {
      setShowSubItems(true);
    }
  }, [hasSubItemsData]);

  useEffect(() => {
    if (hasOrderSettingsData && !showOrderSettings) {
      setShowOrderSettings(true);
    }
  }, [hasOrderSettingsData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingImage(true);
    try {
      const res = await uploadApi.uploadPhoto([file]);
      const uploadedUrl = res?.msgResp?.url || res?.url;
      if (uploadedUrl) {
        setImagePreview(uploadedUrl);
        if (setValue) {
          setValue(`items.${itemIndex}.image`, uploadedUrl);
        }
      }
    } catch (err) {
      console.error('Error uploading product item image:', err);
    } finally {
      setIsUploadingImage(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setImagePreview(null);
    if (setValue) {
      setValue(`items.${itemIndex}.image`, null);
    }
  };

  const handleOrderSettingsChange = (orderSettingsValues) => {
    if (!setValue) return;
    const maxQty = orderSettingsValues.availableStock === '' ? '' : Number(orderSettingsValues.availableStock);
    const limitOrder = orderSettingsValues.limitPerOrder === '' ? '' : Number(orderSettingsValues.limitPerOrder);
    const minOrder = orderSettingsValues.minimumPerOrder === '' ? '' : Number(orderSettingsValues.minimumPerOrder);

    setValue(`items.${itemIndex}.maxQuantity`, maxQty, { shouldValidate: true });
    setValue(`items.${itemIndex}.limitPerOrder`, limitOrder, { shouldValidate: true });
    setValue(`items.${itemIndex}.minOrderQty`, minOrder, { shouldValidate: true });
  };

  const handleRemoveOrderSettings = () => {
    setShowOrderSettings(false);
    if (!setValue) return;
    setValue(`items.${itemIndex}.maxQuantity`, 1000, { shouldValidate: true });
    setValue(`items.${itemIndex}.limitPerOrder`, 1000, { shouldValidate: true });
    setValue(`items.${itemIndex}.minOrderQty`, 1, { shouldValidate: true });
    if (clearErrors) {
      clearErrors(`items.${itemIndex}.maxQuantity`);
      clearErrors(`items.${itemIndex}.limitPerOrder`);
      clearErrors(`items.${itemIndex}.minOrderQty`);
    }
  };

  const handleSplitCaseChange = (splitCaseValues) => {
    if (!setValue) return;
    const currentItemName = watch ? watch(`items.${itemIndex}.name`) : '';
    const currentItemDesc = watch ? watch(`items.${itemIndex}.description`) : '';
    const currentItemImg = watch ? watch(`items.${itemIndex}.image`) : null;

    const isFull =
      splitCaseValues.fulfilmentOption === 'full' ||
      splitCaseValues.fulfillCondition === SPLIT_CASE_CONDITION_FULL;

    const splitCasePayload = {
      name: currentItemName || 'Split case',
      itemsPerCase: Number(splitCaseValues.itemsPerCase || 0),
      costPerItem: Number(splitCaseValues.costPerItem || 0),
      minItemOrder: Number(splitCaseValues.minimumItemOrder || 1),
      isFullCase: isFull,
      fulfillCondition: isFull ? SPLIT_CASE_CONDITION_FULL : SPLIT_CASE_CONDITION_PARTIAL,
      description: currentItemDesc || null,
      photoUrl: typeof currentItemImg === 'string' ? currentItemImg : currentItemImg?.url || '',
      acknowledgementRequiredPayAfter: isFull ?
        Boolean(splitCaseValues.acknowledgePayLater) : false,
    };
    if (!isFull && clearErrors) {
      clearErrors(`items.${itemIndex}.splitCasePurchase.acknowledgementRequiredPayAfter`);
    }
    setValue(`items.${itemIndex}.splitCasePurchase`, splitCasePayload, { shouldValidate: true });
  };

  const handleRemoveSplitCase = () => {
    setShowSplitCase(false);
    if (!setValue) return;
    setValue(`items.${itemIndex}.splitCasePurchase`, null);
  };

  const handleSubItemsChange = (groups) => {
    if (!setValue) return;
    const subItemsPayload = groups.map((g) => ({
      type: g.title || '',
      selectionMode: g.selectionMode || 'single',
      trackInventory: Boolean(g.trackInventory),
      required: g.required ? 1 : 0,
      list: g.choices.map((c) => ({
        name: c.name || '',
        price: Number(c.price || 0),
        ...(g.trackInventory ? { maxQty: c.maxQty === '' ? null : Number(c.maxQty) } : {})
      })),
    }));
    setValue(`items.${itemIndex}.subItems`, subItemsPayload, { shouldValidate: true });
  };

  const handleRemoveSubItems = () => {
    setShowSubItems(false);
    if (!setValue) return;
    setValue(`items.${itemIndex}.subItems`, [], { shouldValidate: true });
    if (clearErrors) {
      clearErrors(`items.${itemIndex}.subItems`);
    }
  };

  const handleOptionClick = (optionType) => {
    setIsDropdownOpen(false);
    if (optionType === 'orderSettings') {
      setShowOrderSettings(true);
      handleOrderSettingsChange(DEFAULT_ORDER_SETTINGS);
    } else if (optionType === 'splitCase') {
      setShowSplitCase(true);
      handleSplitCaseChange(DEFAULT_SPLIT_CASE_SETTINGS);
    } else if (optionType === 'subItem') {
      setShowSubItems(true);
    }
    if (onSelectOption) {
      onSelectOption(optionType);
    }
  };

  const splitCaseValues = formSplitCase
    ? {
        itemsPerCase: formSplitCase.itemsPerCase ?? '',
        costPerItem: formSplitCase.costPerItem ?? '',
        minimumItemOrder: formSplitCase.minItemOrder ?? '1',
        fulfilmentOption: isSplitCaseFull ? 'full' : 'incomplete',
        fulfillCondition: isSplitCaseFull
          ? SPLIT_CASE_CONDITION_FULL
          : SPLIT_CASE_CONDITION_PARTIAL,
        acknowledgePayLater: isSplitCaseFull ?
          Boolean(formSplitCase.acknowledgementRequiredPayAfter) : false,
      }
    : DEFAULT_SPLIT_CASE_SETTINGS;

  const initialSubItemGroups = (formSubItems && formSubItems.length > 0)
    ? formSubItems.map((group, gIdx) => ({
        id: group.id || `group-${gIdx + 1}`,
        title: group.type || '',
        selectionMode: group.selectionMode || 'single',
        trackInventory: Boolean(group.trackInventory),
        required: Boolean(group.required === 1 || group.required === true),
        choices: (group.list || []).map((choice, cIdx) => ({
          id: choice.id || `choice-${cIdx + 1}`,
          name: typeof choice === 'string' ? choice : choice?.name || '',
          price: typeof choice === 'object' && choice?.price !== undefined ? choice.price : '',
          maxQty: typeof choice === 'object' && choice?.maxQty !== undefined && choice?.maxQty !== null ? choice.maxQty : '',
        })),
      }))
    : undefined;

  const hasCustomOrderSettings =
    formMaxQuantity !== undefined ||
    formLimitPerOrder !== undefined ||
    formMinOrderQty !== undefined;

  const orderSettingsValues = hasCustomOrderSettings
    ? {
        availableStock:
          formMaxQuantity !== undefined && formMaxQuantity !== null
            ? String(formMaxQuantity)
            : '1000',
        limitPerOrder:
          formLimitPerOrder !== undefined && formLimitPerOrder !== null
            ? String(formLimitPerOrder)
            : '1000',
        minimumPerOrder:
          formMinOrderQty !== undefined && formMinOrderQty !== null
            ? String(formMinOrderQty)
            : '1',
      }
    : DEFAULT_ORDER_SETTINGS;

  return (
    <div className="product-item" id={`item-${itemNumber}`}>
      <div className="product-item-hd">
        <span className="product-item-num">Item {itemNumber}</span>
        <div className="product-item-actions">
          <div className="icon-btn" title="Move up" onClick={onMoveUp}>
            <FiChevronUp size={12} />
          </div>
          <div className="icon-btn" title="Move down" onClick={onMoveDown}>
            <FiChevronDown size={12} />
          </div>
          <div className="icon-btn" title="Duplicate" onClick={onDuplicate}>
            <FiCopy size={12} />
          </div>
          <div className="icon-btn danger" title="Delete" onClick={onDelete}>
            <FiTrash2 size={12} />
          </div>
        </div>
      </div>

      <div className="product-item-body">
        <div className="field-row">
          <span className="field-lbl">Image</span>
          <label className={`img-upload ${imagePreview ? 'img-upload--has-image' : ''}`}>
            {isUploadingImage ? (
              <span className="img-upload-lbl" style={{ color: '#009A97', fontSize: '11px' }}>
                Uploading…
              </span>
            ) : imagePreview ? (
              <>
                <div className="img-preview-wrapper">
                  <img src={imagePreview} alt="Item preview" />
                </div>
                <button
                  type="button"
                  className="img-upload-remove-btn"
                  onClick={handleRemoveImage}
                  title="Remove image"
                >
                  <FiX size={12} />
                </button>
              </>
            ) : (
              <>
                <FiImage style={{ width: 20, height: 20, stroke: '#80CCCB' }} />
                <span className="img-upload-lbl">Upload</span>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
          </label>
        </div>

        <div className="field-row">
          <span className="field-lbl">
            Name <span style={{ color: '#E24B4A' }}>*</span>
          </span>
          <div className="field-val">
            <input
              type="text"
              placeholder="Item name"
              {...(register ? register(`items.${itemIndex}.name`) : {})}
            />
            {itemError?.name && (
              <span style={{ color: '#E24B4A', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                {itemError.name.message}
              </span>
            )}
          </div>
        </div>

        <div className="field-row">
          <span className="field-lbl">Description</span>
          <div className="field-val">
            <textarea
              placeholder="Optional…"
              style={{ height: '46px' }}
              {...(register ? register(`items.${itemIndex}.description`) : {})}
            />
            {itemError?.description && (
              <span style={{ color: '#E24B4A', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                {itemError.description.message}
              </span>
            )}
          </div>
        </div>

        <div className="field-row">
          <span className="field-lbl">
            Price <span style={{ color: '#E24B4A' }}>*</span>
          </span>
          <div className="field-val">
            <div className="price-row">
              <span className="price-prefix">$</span>
              <input
                type="number"
                placeholder="0.00"
                step="0.50"
                min="0"
                style={{ width: '90px', textAlign: 'right' }}
                {...(register ? register(`items.${itemIndex}.price`) : {})}
              />
            </div>
            {itemError?.price && (
              <span style={{ color: '#E24B4A', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                {itemError.price.message}
              </span>
            )}
          </div>
        </div>

        <div className="options-wrap">
          <div className="options-hd">
            <div
              className="add-option-trigger"
              ref={dropdownRef}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <FiPlus style={{ width: 11, height: 11 }} />
              Add option
              {isDropdownOpen && (
                <div className="option-dropdown">
                  <div
                    className="opt-dd-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionClick('orderSettings');
                    }}
                  >
                    Order Settings
                    <div className="opt-dd-sub">Available stock, limits per order</div>
                  </div>
                  {userInfo?.isUsingFeature && (
                    <div
                      className="opt-dd-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOptionClick('splitCase');
                      }}
                    >
                      Split Case Purchase
                      <div className="opt-dd-sub">Allow partial case orders</div>
                    </div>
                  )}
                  <div
                    className="opt-dd-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionClick('subItem');
                    }}
                  >
                    Add sub-item
                    <div className="opt-dd-sub">e.g. Frame Color, Lens Color</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="opt-panels">
            {showOrderSettings && (
              <OrderSettings
                values={orderSettingsValues}
                onChange={handleOrderSettingsChange}
                onDelete={handleRemoveOrderSettings}
                orderCount={formOrderCount}
                itemError={itemError}
              />
            )}
            {showSplitCase && (
              <SplitcasePurchase
                namePrefix={`sc-item-${itemNumber}`}
                itemPrice={itemPrice}
                values={splitCaseValues}
                onChange={handleSplitCaseChange}
                onDelete={handleRemoveSplitCase}
                error={splitCaseError}
              />
            )}
            {showSubItems && (
              <SubItems
                ref={subItemsRef}
                initialGroups={initialSubItemGroups}
                onChange={handleSubItemsChange}
                onDelete={handleRemoveSubItems}
                error={itemError?.subItems}
                hasOrderCount={Number(formOrderCount) > 0}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
